import { CACHE_CONFIG } from '../config/api';

class CacheService {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.loadFromStorage();
  }

  // Generate cache key
  generateKey(category, options = {}) {
    const params = {
      category,
      limit: options.limit || 8,
      offset: options.offset || 0,
      search: options.search || '',
      label: options.label || '',
      orderfields: options.orderfields || 'created',
      orderby: options.orderby || 'desc'
    };
    return JSON.stringify(params);
  }

  // Check if cache entry is valid
  isValid(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp) return false;
    
    const now = Date.now();
    const age = now - timestamp;
    return age < CACHE_CONFIG.TTL;
  }

  // Get from cache
  get(category, options = {}) {
    const key = this.generateKey(category, options);
    
    if (this.cache.has(key) && this.isValid(key)) {
      return this.cache.get(key);
    }
    
    return null;
  }

  // Set in cache
  set(category, options = {}, data) {
    const key = this.generateKey(category, options);
    
    // Check cache size limit
    if (this.cache.size >= CACHE_CONFIG.MAX_SIZE) {
      this.cleanup();
    }
    
    this.cache.set(key, data);
    this.timestamps.set(key, Date.now());
    this.saveToStorage();
  }

  // Remove from cache
  remove(category, options = {}) {
    const key = this.generateKey(category, options);
    this.cache.delete(key);
    this.timestamps.delete(key);
    this.saveToStorage();
  }

  // Clear cache for a category
  clearCategory(category) {
    const keysToDelete = [];
    
    for (const key of this.cache.keys()) {
      try {
        const parsedKey = JSON.parse(key);
        if (parsedKey.category === category) {
          keysToDelete.push(key);
        }
      } catch (e) {
        // Invalid key format, remove it
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.timestamps.delete(key);
    });
    
    this.saveToStorage();
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.timestamps.clear();
    this.saveToStorage();
  }

  // Cleanup expired entries
  cleanup() {
    const now = Date.now();
    const keysToDelete = [];
    
    for (const [key, timestamp] of this.timestamps.entries()) {
      const age = now - timestamp;
      if (age >= CACHE_CONFIG.TTL) {
        keysToDelete.push(key);
      }
    }
    
    // If no expired entries, remove oldest entries
    if (keysToDelete.length === 0 && this.cache.size >= CACHE_CONFIG.MAX_SIZE) {
      const sortedKeys = Array.from(this.timestamps.entries())
        .sort((a, b) => a[1] - b[1])
        .slice(0, Math.floor(CACHE_CONFIG.MAX_SIZE / 4))
        .map(([key]) => key);
      
      keysToDelete.push(...sortedKeys);
    }
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.timestamps.delete(key);
    });
    
    this.saveToStorage();
  }

  // Load cache from localStorage
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(CACHE_CONFIG.STORAGE_KEY);
      if (stored) {
        const { cache, timestamps } = JSON.parse(stored);
        
        // Convert arrays back to Maps
        this.cache = new Map(cache);
        this.timestamps = new Map(timestamps);
        
        // Cleanup expired entries
        this.cleanup();
      }
    } catch (error) {
      console.warn('Failed to load cache from storage:', error);
      this.cache = new Map();
      this.timestamps = new Map();
    }
  }

  // Save cache to localStorage
  saveToStorage() {
    try {
      const data = {
        cache: Array.from(this.cache.entries()),
        timestamps: Array.from(this.timestamps.entries())
      };
      
      localStorage.setItem(CACHE_CONFIG.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save cache to storage:', error);
    }
  }

  // Get cache statistics
  getStats() {
    return {
      size: this.cache.size,
      maxSize: CACHE_CONFIG.MAX_SIZE,
      ttl: CACHE_CONFIG.TTL,
      oldestEntry: this.timestamps.size > 0 ? Math.min(...this.timestamps.values()) : null,
      newestEntry: this.timestamps.size > 0 ? Math.max(...this.timestamps.values()) : null
    };
  }
}

// Create singleton instance
const cacheService = new CacheService();

export default cacheService;
